import React from "react";

import { DeepDiveContent } from "../styles.DeepDive";
import { Headline } from "../DeepDive";
import { Quote } from "../../quote/Quote";

import marv1 from "../../../assets/marv.png";
import marv2 from "../../../assets/marv.gif";
import FactBox from "../../../assets/FactBox.png";
import Graph from "../../../assets/Graph.png";
import GraphRDF from "../../../assets/GraphSchematics.svg";
import Prompt from "../../../assets/gpt3.mp4";

export const QualityCriteria: React.FC = ({}) => {
  return (
    <DeepDiveContent>
      <Headline authorName="Rebecca Ciesielski">
        Quality Criteria for Fact Boxes
      </Headline>

      <p>
        Fact boxes are made of facts linked to each other, which is far from
        trivial. Calling something a fact requires significant effort in
        journalism and even more so in science. How is it possible to assess
        whether a claim or proposition taken from scientific research on climate
        change is valid and not biased? And how do we determine whether the
        presentation of the facts actually reflects the current scientific
        discourse on a topic and does not distort the relevance of the findings?
        The answer is by ensuring that each fact box meets two basic criteria.
      </p>
      <ul>
        <li>
          Each fact must be true on the basis of the latest scientific knowledge
        </li>
        <li>The fact must be intrinsically correct and not biased</li>
      </ul>
      <p>
        Fact boxes about scientific topics, such as climate change in our case,
        can be seen as the essence of a longer article covering a certain topic.
        Consequently, the fact boxes and their source references are both
        subject to the same quality criteria. Those criteria are guided by the
        rules of science itself. Loosely based on Karl Popper – Scientific
        knowledge and thus provable facts are created by applying scientific
        methods that are verifiable.
      </p>
      <p>
        This demand for verifiability has become institutionalised, eg. in the
        form of scientific journals and impact factors. These indicators are
        used to evaluate the work of individual scientists. Those who get cited
        and published a lot in renowned journals receive higher impact factors.
        Besides scientists, scientific journals themselves are subject to these
        evaluation mechanisms. The impact factor of a certain journal increases
        by citation per published article. Even if all of these measures are
        somehow problematic, in times of predatory journals and fake science
        they nevertheless provide orientation for science reporting.
      </p>
      <p>
        That is why professional science journalism knows these mechanisms and
        their limits very well. The SMC as a specialist editorial office in
        science journalism, has developed various strategies to select the
        appropriate experts and facts for each topic – in both cases with the
        aim of presenting the current state of scientific knowledge. Therefore,
        the SMC has created a large database of experts, which is constantly
        updated and also several routines to monitor new developments in
        different fields of science.
      </p>
      <p>
        Each of these experts is researched on the basis of scientific and
        journalistic criteria as follows. In which journals has a scientist
        published and on which topic exactly? Can their reputation be further
        validated by participation at congresses or membership in specialised
        committees or societies? Which other interests could play a role in the
        experts view on a certain topic like conflict of interest such as
        patents?
      </p>
      <p>
        This approach to science reporting makes SMC features particularly good
        sources for fact boxes. Each fact comes from a trustable source, either
        the SMC itself with due regard to journalistic standards or carefully
        selected scientific experts.
      </p>
    </DeepDiveContent>
  );
};

export const Anatomy: React.FC = ({}) => {
  return (
    <DeepDiveContent>
      <Headline authorName="Cécile Schneider">Anatomy of a Fact Box</Headline>
      <p>
        Fact boxes are a regularly used element in journalistic layouts. In the
        few research papers we found, we learned that fact boxes can{" "}
        <a
          target="_blank"
          rel="noopener noreferrer" 
          href="https://www.researchgate.net/publication/228679767_The_role_of_local_design_factors_for_newspaper_reading_behaviour_An_eye_tracking_perspective"
        >
          {" "}
          attract the reader’s eye in print layouts
        </a>
        ,{" "}
        <a
          target="_blank"
          rel="noopener noreferrer" 
          href="https://www.bmj.com/content/357/bmj.j2460.full"
        >
          {" "}
          help make more risk-aware decisions
        </a>{" "}
        about medical treatment, and{" "}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://medium.com/trust-media-and-democracy/america-the-clueless-or-america-the-context-less-3c6579c407e"
        >
          provide context to reduce partial bias
        </a>
        .
      </p>

      <p>
        The advantage of fact boxes is their modular nature. They can be used
        and reused in different articles and contexts, giving readers a certain
        foundation or background knowledge.
      </p>
      <p>
        The user-facing side of a BR fact box consists of a headline and an
        explanatory text. It can be embedded into an existing article via
        iframe. In the editor-facing backend, a few more metadata are available,
        like the date when the box was created and updated, the authors and
        editors, keyword tags, and links to the sources used for the fact box.
      </p>

      <img
        className="width-eighty content"
        src={FactBox}
        alt="Image of a fact box as published on BR24."
      />

      <p>
        The newsroom editors are able to find fact boxes on a given topic by
        searching for keywords in the fact database which is accessible through
        a simple browser plug-in. They can choose from finished pieces without
        adding extra workload to the creation of their article. Instead, they
        can draw from a well-kept stack of fact boxes to enrich up-to-date
        reporting.
      </p>
      <p>
        This happens with the help of different internal teams working together
        alongside a start-up called FactFox. FactFox emerged from BR’s newsroom
        in order to solve the context challenge and provides the technical basis
        for creating, matching and embedding facts into articles and community
        discussions.
      </p>
    </DeepDiveContent>
  );
};

export const Propositions: React.FC = ({}) => {
  return (
    <DeepDiveContent>
      <Headline authorName="Meik Bittkowski">
        Propositions as Reusable Information
      </Headline>
      <p>
        To (very) roughly paraphrase John F. Kennedy: ask not what propositions
        are, but what they can do for you!
      </p>
      <p>
        For the knowledge graph, the SMC extracts what is said by scientists
        about scientific issues from statements given in a certain context. In
        the process of annotating the statements with propositions, the content
        of the statements is detached from the concrete context and
        justifications for the assertions are made explicit.
      </p>
      <p>
        A small example will illustrate the annotation of statements with
        propositions. Assume that the knowledge graph contains the following
        (abridged) statement by a scientist about the connection between land
        use and climate change:
      </p>

      <Quote>
        A recent study by our institute [1] shows that the question of whether
        reforestation ('natural succession') or bioenergy offers the greater
        advantages for the climate when replacing fossil energy depends strongly
        on the technologies of bioenergy use as well as on the respective
        geographical and contextual circumstances. [...] The results show that
        bioenergy is not always the best possible contribution to climate
        protection that can be achieved by using an available area. [...]
      </Quote>

      <p>
        Then the following proposition can be derived from this statement:
        "Bioenergy is not always the best possible contribution to climate
        protection that can be achieved by using an available area". This
        proposition is supported by at least two things. First, by the statement
        of a scientist, and second, by a referenced scientific publication (via
        “[1]”). Of course, there may be other statements or literature
        references in the knowledge graph that support or oppose these
        propositions. Thus, propositions can bundle both justifications and
        criticism about statements on scientific topics.
      </p>
      <p>
        The question of what propositions actually are leads us directly into
        the deepest waters of Western metaphysics – if you want to venture into
        a really deep dive, take a look at the following article in the{" "}
        <a
          target="_blank"
          href="https://plato.stanford.edu/entries/propositions/"
        >
          {" "}
          Stanford Encyclopedia of Philosophy
        </a>
        .
      </p>
      <p>
        As a first approximation, propositions can be defined as the sharable
        objects of attitudes such as what is believed, doubted, feared, etc.,
        that make beliefs and other attitudes true or false.
      </p>
      <p>
        If a scientist believes, for example, that soil sealing leads to greater
        damage from heavy rain, she believes the following proposition: Soil
        sealing leads to greater damage from heavy rain.
      </p>
      <p>
        It is important to note that one and the same proposition can be
        expressed in very different ways, even across languages. The German
        sentence "Ich glaube, dass Bodenversiegelung zu stärkeren Schäden durch
        Starkregen führt" expresses the same proposition as the English sentence
        above.
      </p>
      <p>
        Propositions about scientific topics are not statements about facts,
        because they could also be false. But propositions can be supported by
        facts, and therefore they can be justified and hence can be considered
        true in the light of current scientific knowledge. This is the case for
        most of the propositions we have extracted.
      </p>
      <p>
        However, they can also be controversial within science. One scientist
        believes something is the case, while another doubts it. It is a
        strength of propositions (as we use them) that they allow us to map
        disagreement within the sciences at a high level of abstraction. The
        following illustration models the structure of propositions in a
        knowledge graph.
      </p>

      <img
        className="content"
        src={GraphRDF}
        alt="Schematic View of an RDF knowledge graph."
      />
    </DeepDiveContent>
  );
};

export const RepresentingKnowledge: React.FC = ({}) => {
  return (
    <DeepDiveContent>
      <Headline authorName="Meik Bittkowski">
        Representing Knowledge in Graphs
      </Headline>
      <p>
        Let’s try to give a simple, not too technical definition of knowledge
        graphs: a knowledge graph represents a collection of interlinked
        descriptions of entities. The term "entities" means things in the most
        general sense: objects, events, concepts, ideas and others.
      </p>
      <p>
        An example from art history may help to illustrate how knowledge is
        represented in knowledge graphs.
      </p>

      <img
        className="content"
        src={Graph}
        alt="Schematic view of a knowledge graph."
      />

      <p>
        What are the entities described in this exemplary knowledge graph? There
        are persons, possibly classified into artists and philosophers, works of
        art, events like the historical trial of Socrates and institutions like
        art-historical collections. The means of description are names, labels
        and pictures, which are used partly to illustrate and partly to depict
        entities.
      </p>
      <p>
        The distinction between the descriptions of an entity and the entity
        described is crucial for knowledge graphs to represent knowledge about
        the world. The node in the knowledge graph with the label "School of
        Athens" uses an image of the artwork as description but refers to the
        real thing: the fresco with the Italian title "Scuola di Atene", painted
        by Raphael in 1509-1511 on an area of 5x7 metres in the Apostolic Palace
        in the Vatican.
      </p>
      <p>
        The relationship between the real painting named “School of Athens" and
        the real artist named "Raphael" is, among many other relationships, also
        represented in the depicted knowledge graph: "School of Athens" has the
        relation "creator" to "Raphael".
      </p>
      <p>
        With the help of triplets of nodes and relationships, various facts can
        be derived from the knowledge graph:
      </p>

      <ul>
        <li>School of Athens depicts Socrates</li>
        <li>School of Athens (has) creator Raphael</li>
        <li>The Death of Socrates (has) main subject Socrates</li>
        <li>Trial of Socrates (has) participant Socrates</li>
      </ul>
      <p>
        Against the set of derivable facts of a knowledge graph, any query can
        be made to answer questions about the represented body of knowledge.
        These queries can be simple, such as: "Which philosopher is depicted in
        the artwork 'School of Athens'?" (Answer: "Socrates") However, queries
        can also become quite complex, such as: "Which artist is the creator of
        a work of art that has as its main subject a participant in the trial of
        Socrates?" (Answer “Jacques-Louis David”).
      </p>

      <p>
        We can see from this small example how knowledge graphs structure
        information. The fact that knowledge graphs can be queried in a very
        flexible way makes them extremely interesting as a means of knowledge
        representation for modular and structured journalistic uses.
      </p>
    </DeepDiveContent>
  );
};

export const SentenceVectors: React.FC = ({}) => {
  return (
    <DeepDiveContent>
      <Headline authorName="Marco Lehner">Sentence Embeddings</Headline>
      <p>
        In natural language processing, vectors are often used as a mathematical
        form to represent a word, a sentence, or even a full document. We will
        look at the easiest example, the word vector, and go from there to the
        more elaborate concepts.
      </p>
      <p>
        Vectors are basically arrows which have a length and an orientation. For
        word vectors, the orientation is the most important feature of a vector.
        Two similar words should be represented by vectors with similar
        orientation – they should point to coordinates which are close together.
      </p>
      <p>
        To measure how similar two vectors are we use cosine similarity, which
        describes the angle between two vectors. Vectors with the same
        orientation have a cosine similarity of 1, vectors with completely
        different orientation (orthogonal vectors) have a cosine similarity of
        0.
      </p>
      <p>
        Let’s look at an example: If we pass the words “Oak” and “Pine” to a
        model which produces word vectors, we would expect the vectors to be
        quite similar. And indeed, the cosine similarity between the vectors of
        Oak and Pine is 0.67. When we look at other kinds of trees we get
        equally high similarity values. If we compare the vector for “Oak” and
        the pop singer Shakira, we get a cosine similarity of 0.01 – as we would
        expect they are pretty far apart. If you want to play around a bit with
        your own words, you can do this{" "}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="http://bionlp-www.utu.fi/wv_demo/"
        >
          here
        </a>
        .
      </p>
      <p>
        This is what we mean, when we say the vector represents the word.
        Vectors which stand for related words have a high cosine similarity. The
        vectors themselves have no meaning but they can be compared and get
        meaning in relation to other word representations. This basic principle
        applies to word vectors as well as the sentence vectors we have used,
        and document vectors.
      </p>
    </DeepDiveContent>
  );
};

export const Evaluation: React.FC = ({}) => {
  return (
    <DeepDiveContent>
      <Headline authorName="Marco Lehner and Rebecca Ciesielski">
        Evaluation of generated text
      </Headline>
      <p>
        The evaluation of the generated text was completed mostly with manual
        labour. A qualified person needs to compare input and output carefully.
        Therefore, we split our evaluation in two streams. In the first one, we
        as a team rated 20 automatically generated texts in three categories
        that are most important to us.
      </p>

      <ul>
        <li>Correctness</li>
        <li>Grammar</li>
        <li>Narration</li>
      </ul>

      <p>
        In the second stream, we gave three automatically generated mock-up fact
        boxes to our colleagues in the archive. Of these, one was completely
        auto-generated and two were generated with some little human tweaks for
        readability.
      </p>

      <h3>Correctness</h3>
      <p>
        Correctness describes mistakes in the model output that contradict or
        distort the facts, or add hallucinations to the given input. From our 20
        auto-generated texts, only 4 received a flawless rating for factual
        correctness. In the remaining texts, we encountered several issues why
        we concluded that human supervision is still mandatory in a journalistic
        workflow:
      </p>

      <ul>
        <li>
          GPT-3 changed some propositions to the point of distorting their
          entire meaning, e. g.: “The simplified ESCIMO model progresses in
          climate change even if...” In the initial proposition it is not the
          model that is progressing;
        </li>
        <li>
          It occasionally used colloquial German wording while the probably
          unknown scientific term “Landsenke” (depression) was ignored several
          times;
        </li>
        <li>
          It completely changed some facts and sometimes even incorporated
          hallucinated numbers, which were not given in the input at all;
        </li>
        <li>
          It invented and inserted semantic connections, back references,
          comparisons, etc., which sometimes distorted whole statements;
        </li>
        <li>
          It hallucinated entire explanations, e.g.: “Due to an acquired
          resistance to drought, plants tend to be more fertile – but this is
          associated with a greater risk of dying during drought years.”;
        </li>
        <li>
          It newly introduced complicated terms without explaining them, e. g.
          “... as cascades of concentration and reaction occur.”;
        </li>
        <li>Occasionally, facts from the propositions were left out.</li>
      </ul>

      <p>
        Most of these mistakes are hard to recognise if the evaluating person
        does not have in-depth knowledge of the subject. Therefore we recommend
        the person who evaluates the model’s output must be properly trained on
        the input as well.
      </p>
      <p>
        One possible option to help fact-checking texts is the API of{" "}
        <a target="_blank" rel="noopener noreferrer" href="https://www.factiverse.no/">
          Factiverse
        </a>{" "}
        for instance, which supports the fact-checking process and assigns a
        score representing the trustworthiness of a given claim by comparing it
        to information on the websites of leading newspapers. Still, human
        supervision is mandatory in such a process.
      </p>

      <h3>Grammar</h3>
      <p>
        GPT-3 did not always generate grammatically correct German text. We saw
        grammatical errors and unfitting formulations in our generated texts.
        The problem of grammatically incorrect text is not as difficult as wrong
        facts. Grammatical errors are easy to spot for trained journalists and
        they can be corrected within existing newsroom workflows.
      </p>
      <p>
        This problem could also be improved in the future by passing GPT-3’s
        output to a grammar checker, like{" "}
        <a
          target="_blank"
          rel="noopener noreferrer" 
          href="https://github.com/languagetool-org/languagetool"
        >
          LanguageTool
        </a>
        . Yet, this solution requires a second service running, since all
        available grammar checkers are quite complex pieces of software.
      </p>

      <h3>Narration</h3>
      <p>
        A fact box should answer a certain question or shed light on a specific
        fact. This becomes a problem if the retrieved propositions are relevant
        to the article, but cover different aspects of it. Furthermore, our
        propositions lack logical connection between them and do not necessarily
        come out in the best order.
      </p>
      <p>
        GPT-3 understandably struggled with this difficult task but tried to
        make sense out of the propositions supplied to it. Still, we saw
        improved coherence on the text level in 12 of 20 generated texts, but we
        also saw narrative leaps, which rendered the fact boxes unusable.
      </p>
      <h3>Feedback from the intended users</h3>
      <p>
        We critically examined the model’s output working with documentalists
        from the BR’s archive, and tested the output for practical usefulness as
        part of the archive’s workflow. After all, each generated text should
        ideally provide a useful and particularly time-saving basis for the fast
        and efficient creation of a fact box.
      </p>
      <p>
        We shared three automatically generated texts with them. Two were
        partially automatically generated; the third text was written by GPT-3.
      </p>

      <p>
        The archive colleagues expressed several points of criticism. For once,
        they aimed to have fact boxes that are simpler than the corresponding
        article, and found the complexity of the generated fact boxes too high.
        They also would have included more context.
      </p>

      <p>
        The most significant problem was the unreliable content. To meet their
        work standard, every aspect needs to be verified. Therefore, errors
        generated by the system were perceived as a serious dilemma:
        “Troubleshooting was enormously time-consuming.” Overall, the fact boxes
        were not rated as making work easier. The search for errors was
        perceived as more time-consuming than the established workflow of
        writing fact boxes manually.
      </p>

      <p>
        From the points of criticism, we were able to derive suggestions for
        improvement. Overall, the respondents said they would be happy with an
        excerpt of what the SMC provides in a more raw format. This meant the
        specific context and occasion of a climate phenomenon in question, in
        combination with an introductory text and a list of propositions which
        are linked to their corresponding expert statements. Such a dossier can
        be generated and matched from the knowledge graph without
        post-processing the text by GPT-3 and could be more easily integrated
        into the archive’s workflow.
      </p>
    </DeepDiveContent>
  );
};

export const PromptDesign: React.FC = ({}) => {
  return (
    <DeepDiveContent>
      <Headline authorName="Philipp Gawlik">
        GPT-3 Prompt Design for Journalists
      </Headline>
      <p>
        GPT-3’s interface looks very much like a journalist’s work space. It is
        a blinking cursor in an empty document, the so-called prompt. To tell
        the language model what you want it to do, you simply give one or two
        examples of what the input is and how the output should look. This
        approach is called "Few Shot Learning" with the examples being denoted
        as “a few shots” and the ability of the language model to adapt its text
        generation behaviour to the examples.This interaction with the model is
        particularly easy to do for journalists, because they interact with
        GPT-3 in written natural language instead of programming code.
      </p>
      <p>
        A common approach to begin a prompt design is to specify the task at
        hand in a straightforward way (see examples 1-2).
      </p>
      <p>Ex. 1: Marv is a chatbot that reluctantly answers questions.</p>
      <p>
        Ex. 2: Convert docstring function descriptions into a javascript
        function.
      </p>
      <p>
        In addition to the task specification a few examples are given from
        which the language model can start to generate new text. See example 3.
      </p>

      <img
        className="width-fifty content"
        src={marv1}
        alt="Marv the depressive robot as a chat bot."
      />

      <p>
        From here on, one would trigger the language model’s generation
        abilities with the prefix given in Ex. 4 and collect the generated text
        from here.
      </p>

      <img
        className="width-fifty content"
        src={marv2}
        alt="Marv the depressive robot as a chat bot."
      />
      <p>
        In addition to the text prompt, a technical configuration can be passed
        to the language model that includes threshold values concerning
        creativity, term repetition, generation length, number of generation
        attempts and term markers where to start and stop to generate output, as
        well as the version of the language model to use. A best practise for
        finding good threshold values for the task at hand is to set the
        threshold values to zero, writing a prompt and then gradually raising
        the thresholds while evaluating the model output.
      </p>
      <p>
        Now that we know how the model works, how do we adapt the prompt to our
        task of creating our fact boxes?
      </p>
      <p>
        We consider the input and the output we want to generate from the model.
        The input is a set of propositions (single/multiple sentences of the
        same topic) and the output should be a coherent combination of the
        propositions to a text fit to publish in a fact box.
      </p>
      <p>
        The idea for our prompt design can be described as a cloze exercise
        designed for a science journalist to fill in the missing language. We
        wanted GPT-3 to preserve the facts of the propositions while finding
        smooth transitions between the propositions exactly like a journalist
        would do. Therefore we removed some types of words like conjugations,
        articles and stopwords from the input and let GPT-3 fill in the textual
        blanks in between.
      </p>
      <p>
        The first step in our prompt design was to tell GPT-3 what we wanted
        (see Ex. 5). In addition, we gave some examples of how to do that (see
        Ex. 6). The cloze examples were generated from a set of propositions and
        we experimented with several heuristics based on the word properties to
        decide which word/phrase should be kept or replaced by a blank marker{" "}
        {"<>"}.
      </p>
      <video className="content" controls muted src={Prompt}></video>
      <p>
        The immediate result made us experiment with heuristics that decide
        which word to blank out, as well as interpunctuation, to structure the
        factbox text. We spotted a strong effect of the topical closeness when
        multiple propositions were combined. The closer the individual,
        successive propositions were thematically, the more coherent was the
        generated text. Therefore we tried to choose sets of fitting
        propositions so the text gaps for the language model to fill were as
        small as possible.
      </p>
    </DeepDiveContent>
  );
};

export const Metadata: React.FC = ({}) => {
  return (
    <DeepDiveContent>
      <Headline authorName="Meik Bittkowski">
        Joy and Burden of Metadata
      </Headline>
      <p>
        By transforming unstructured texts into a knowledge graph we are using
        metadata i.e further data to describe the properties of data. This not
        only ensures the correct use of the data but in a sense, allows the
        metadata to free the original data from the narrow context of its actual
        use and specify a kind of rule set for the possible, future uses of that
        data.
      </p>
      <p>
        But sadly data does not annotate itself with metadata. Using metadata
        means additional work and creates an increased maintenance effort. If,
        for example, metadata is used to establish a context for scientific
        terms, then this context must be kept as up-to-date as possible.
      </p>
      <p>
        This challenges news organisations to strike a balance between chasing
        the news, which is their true profession, and revising metadata, which
        is more the profession of archives or libraries.
      </p>
      <p>
        Large media companies may be lucky enough to have the resources to meet
        both requirements. As for the others they have the option of sharing the
        work with other organisations they trust, with or without the support of
        intermediary trust brokers such as the SMC.
      </p>
    </DeepDiveContent>
  );
};
